import React from 'react';
import { useTranslation } from 'react-i18next';
import { X, Activity, CheckCircle, XCircle, Clock, Server } from 'lucide-react';

interface EchoTestModalProps {
  visible: boolean;
  onClose: () => void;
  serverInfo: any;
  testResult: any;
  loading: boolean;
  onTest: () => void;
}

const EchoTestModal: React.FC<EchoTestModalProps> = ({
  visible,
  onClose,
  serverInfo,
  testResult,
  loading,
  onTest,
}) => {
  const { t } = useTranslation(['Organization']);
  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
              <Activity className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {t('servers.echo.title', {
                  host:
                    serverInfo?.server_host || t('servers.echo.serverFallback'),
                })}
              </h2>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {/* Test Button */}
          <div className="mb-6">
            <button
              onClick={onTest}
              disabled={loading}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  {t('servers.echo.testing')}
                </>
              ) : (
                <>
                  <Activity className="h-4 w-4" />
                  {t('servers.echo.button')}
                </>
              )}
            </button>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              {t('servers.echo.hint')}
            </p>
          </div>

          {/* Result */}
          {testResult && (
            <div className="space-y-4">
              {/* Success/Error Status */}
              <div
                className={`p-4 rounded-lg border ${
                  testResult.success
                    ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700'
                    : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700'
                }`}
              >
                <div className="flex items-start gap-3">
                  {testResult.success ? (
                    <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <h3
                      className={`text-sm font-medium mb-1 ${
                        testResult.success
                          ? 'text-green-900 dark:text-green-300'
                          : 'text-red-900 dark:text-red-300'
                      }`}
                    >
                      {testResult.success
                        ? 'Connection Successful'
                        : 'Connection Failed'}
                    </h3>
                    <p
                      className={`text-sm ${
                        testResult.success
                          ? 'text-green-800 dark:text-green-400'
                          : 'text-red-800 dark:text-red-400'
                      }`}
                    >
                      {testResult.message}
                    </p>
                  </div>
                </div>
              </div>

              {/* Response Time */}
              {testResult.response_time_ms && (
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                      Response Time
                    </h4>
                  </div>
                  <p className="text-2xl font-semibold text-blue-600 dark:text-blue-400">
                    {testResult.response_time_ms.toFixed(2)} ms
                  </p>
                </div>
              )}

              {/* Server Info */}
              {testResult.server_info && (
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Server className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                      Server Information
                    </h4>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">
                        IP:
                      </span>
                      <span className="ml-2 font-medium text-gray-900 dark:text-white">
                        {testResult.server_info.ip}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">
                        Port:
                      </span>
                      <span className="ml-2 font-medium text-gray-900 dark:text-white">
                        {testResult.server_info.port}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">
                        AE Title:
                      </span>
                      <span className="ml-2 font-medium text-gray-900 dark:text-white">
                        {testResult.server_info.ae_title}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">
                        TLS:
                      </span>
                      <span
                        className={`ml-2 font-medium ${
                          testResult.server_info.tls_enabled
                            ? 'text-green-600 dark:text-green-400'
                            : 'text-gray-600 dark:text-gray-400'
                        }`}
                      >
                        {testResult.server_info.tls_enabled
                          ? 'Enabled'
                          : 'Disabled'}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Supported Query Levels */}
              {testResult.supported_query_levels && testResult.success && (
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                    Supported Query Levels
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    {Object.entries(testResult.supported_query_levels).map(
                      ([level, supported]: [string, any]) => (
                        <div
                          key={level}
                          className={`flex items-center justify-between p-3 rounded-lg border ${
                            supported
                              ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700'
                              : 'bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600'
                          }`}
                        >
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {level}
                          </span>
                          {supported ? (
                            <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                          ) : (
                            <XCircle className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                          )}
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}

              {/* Errors */}
              {!testResult.success && (
                <div className="bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-700 p-4">
                  <h4 className="text-sm font-medium text-red-900 dark:text-red-300 mb-2">
                    Error Details
                  </h4>
                  <p className="text-sm text-red-800 dark:text-red-400 font-mono">
                    {testResult.message || 'Connection failed'}
                  </p>
                </div>
              )}
            </div>
          )}

          {!testResult && !loading && (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              No test results yet. Click "Run Echo Test" to begin.
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors"
          >
            {t('servers.echo.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EchoTestModal;
